import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { ArrowLeft, Save, Plus, Trash2, Type } from 'lucide-react';
import { updateSettings, uploadMedia } from '@/utils/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

// Componente reutilizable para opciones de estilo de texto
const TextStyleOptions = ({ style, onChange, label }) => {
  const currentStyle = style || { fontFamily: 'Inter', textAlign: 'left', fontSize: 'base' };
  
  return (
    <div className="mt-2 p-3 rounded-lg border border-slate-200 bg-slate-50">
      <div className="flex items-center gap-2 mb-2">
        <Type size={14} className="text-slate-500" />
        <span className="text-xs font-medium text-slate-600">Estilo de {label}</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-xs text-slate-500">Tipografía</label>
          <select
            value={currentStyle.fontFamily || 'Inter'}
            onChange={(e) => onChange({ ...currentStyle, fontFamily: e.target.value })}
            className="w-full mt-1 rounded border border-slate-300 px-2 py-1 text-xs"
          >
            <option value="Inter">Inter</option>
            <option value="Arial">Arial</option>
            <option value="Calibri">Calibri</option>
            <option value="Helvetica">Helvetica</option>
            <option value="Georgia">Georgia</option>
            <option value="Times New Roman">Times New Roman</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500">Alineación</label>
          <select
            value={currentStyle.textAlign || 'left'}
            onChange={(e) => onChange({ ...currentStyle, textAlign: e.target.value })}
            className="w-full mt-1 rounded border border-slate-300 px-2 py-1 text-xs"
          >
            <option value="left">Izquierda</option>
            <option value="center">Centro</option>
            <option value="justify">Justificado</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500">Tamaño</label>
          <select
            value={currentStyle.fontSize || 'base'}
            onChange={(e) => onChange({ ...currentStyle, fontSize: e.target.value })}
            className="w-full mt-1 rounded border border-slate-300 px-2 py-1 text-xs"
          >
            <option value="sm">Pequeño</option>
            <option value="base">Normal</option>
            <option value="lg">Grande</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export const AdminSettings = () => {
  const { user } = useAuth();
  const { settings, refreshSettings } = useSettings();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/admin');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(formData);
      toast.success('Configuración guardada');
      refreshSettings();
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const result = await uploadMedia(file);
      setFormData((prev) => ({
        ...prev,
        theme: { ...prev.theme, logo: result.url },
      }));
      toast.success('Logo subido');
    } catch (error) {
      console.error('Logo upload failed:', error);
      toast.error('Error al subir logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const [uploadingVideo, setUploadingVideo] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(0);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Por favor selecciona un archivo de video');
      return;
    }

    setUploadingVideo(true);
    setUploadProgress(0);
    try {
      const result = await uploadMedia(file, (progress) => {
        setUploadProgress(progress);
      });
      setFormData((prev) => ({
        ...prev,
        hero: { ...prev.hero, videoUrl: result.url },
      }));
      toast.success('Video subido correctamente');
    } catch (error) {
      console.error('Video upload failed:', error);
      toast.error('Error al subir video. Intenta con un archivo más pequeño.');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  const addStat = () => {
    setFormData((prev) => ({
      ...prev,
      company: {
        ...prev.company,
        stats: [...(prev.company.stats || []), { value: '', label: '' }],
      },
    }));
  };

  const updateStat = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      company: {
        ...prev.company,
        stats: prev.company.stats.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
      },
    }));
  };

  const removeStat = (index) => {
    setFormData((prev) => ({
      ...prev,
      company: {
        ...prev.company,
        stats: prev.company.stats.filter((_, i) => i !== index),
      },
    }));
  };

  const addService = () => {
    setFormData((prev) => ({
      ...prev,
      services: [...(prev.services || []), { icon: 'Warehouse', title: '', description: '' }],
    }));
  };

  const updateService = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    }));
  };

  const removeService = (index) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
    }));
  };

  const addFooterLink = () => {
    setFormData((prev) => ({
      ...prev,
      footer: {
        ...prev.footer,
        links: [...(prev.footer?.links || []), { label: '', href: '' }],
      },
    }));
  };

  const updateFooterLink = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      footer: {
        ...prev.footer,
        links: prev.footer.links.map((l, i) => (i === index ? { ...l, [field]: value } : l)),
      },
    }));
  };

  const removeFooterLink = (index) => {
    setFormData((prev) => ({
      ...prev,
      footer: {
        ...prev.footer,
        links: prev.footer.links.filter((_, i) => i !== index),
      },
    }));
  };

  // Features (Quiénes Somos cards)
  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      about: {
        ...prev.about,
        features: [...(prev.about?.features || []), { icon: 'MapPinned', title: '', description: '' }],
      },
    }));
  };

  const updateFeature = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      about: {
        ...prev.about,
        features: prev.about.features.map((f, i) => (i === index ? { ...f, [field]: value } : f)),
      },
    }));
  };

  const removeFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      about: {
        ...prev.about,
        features: prev.about.features.filter((_, i) => i !== index),
      },
    }));
  };

  if (!formData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-slate-600">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="container-safe flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/dashboard"
              className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100"
            >
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-lg font-semibold text-slate-900">Configuración del Sitio</h1>
          </div>

          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            <Save size={16} className="mr-2" />
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="container-safe py-8">
        <Tabs defaultValue="empresa" className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="empresa">Empresa</TabsTrigger>
            <TabsTrigger value="contacto">Contacto</TabsTrigger>
            <TabsTrigger value="diseno">Diseño</TabsTrigger>
            <TabsTrigger value="contenido">Contenido</TabsTrigger>
            <TabsTrigger value="paginas">Páginas</TabsTrigger>
            <TabsTrigger value="redes">Redes</TabsTrigger>
          </TabsList>

          {/* Empresa Tab */}
          <TabsContent value="empresa" className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900">Información de la Empresa</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="companyName">Nombre de la Empresa</Label>
                  <Input
                    id="companyName"
                    value={formData.company?.name || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        company: { ...formData.company, name: e.target.value },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="tagline">Tagline</Label>
                  <Input
                    id="tagline"
                    value={formData.company?.tagline || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        company: { ...formData.company, tagline: e.target.value },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={formData.company?.description || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        company: { ...formData.company, description: e.target.value },
                      })
                    }
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Sección Quiénes Somos */}
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900">Sección "Quiénes Somos"</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="aboutTitle">Título de "Quiénes Somos"</Label>
                  <Input
                    id="aboutTitle"
                    value={formData.about?.title || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        about: { ...formData.about, title: e.target.value },
                      })
                    }
                    placeholder="Desarrollo de infraestructura estratégica"
                  />
                  <TextStyleOptions
                    label="título"
                    style={formData.about?.titleStyle}
                    onChange={(newStyle) =>
                      setFormData({
                        ...formData,
                        about: { ...formData.about, titleStyle: newStyle },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="aboutDescription">Descripción</Label>
                  <Textarea
                    id="aboutDescription"
                    value={formData.about?.description || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        about: { ...formData.about, description: e.target.value },
                      })
                    }
                    rows={6}
                    placeholder="Descripción de quiénes somos..."
                  />
                  <TextStyleOptions
                    label="descripción"
                    style={formData.about?.descriptionStyle}
                    onChange={(newStyle) =>
                      setFormData({
                        ...formData,
                        about: { ...formData.about, descriptionStyle: newStyle },
                      })
                    }
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Este texto aparecerá en la sección "Quiénes Somos" del home.
                  </p>
                </div>
              </div>
            </div>

            {/* Tarjetas de características (Ubicación, Flexibilidad, Calidad) */}
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900">Características Destacadas</h3>
              <p className="mt-1 text-sm text-slate-600">
                Las 3 tarjetas que aparecen debajo de "Quiénes Somos" (Ubicación estratégica, Flexibilidad, Calidad y seguridad).
              </p>
              <div className="mt-4 space-y-4">
                {formData.about?.features?.map((feature, idx) => (
                  <div key={idx} className="rounded-lg border border-slate-200 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Característica {idx + 1}</span>
                      <Button variant="outline" size="sm" onClick={() => removeFeature(idx)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    <div>
                      <Label>Icono</Label>
                      <select
                        value={feature.icon || 'MapPinned'}
                        onChange={(e) => updateFeature(idx, 'icon', e.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                      >
                        <option value="MapPinned">Ubicación (MapPinned)</option>
                        <option value="Warehouse">Almacén (Warehouse)</option>
                        <option value="ShieldCheck">Seguridad (ShieldCheck)</option>
                        <option value="Building2">Edificio (Building2)</option>
                        <option value="HardHat">Construcción (HardHat)</option>
                      </select>
                    </div>
                    <div>
                      <Label>Título</Label>
                      <Input
                        value={feature.title || ''}
                        onChange={(e) => updateFeature(idx, 'title', e.target.value)}
                        placeholder="Ej: Ubicación estratégica"
                      />
                    </div>
                    <div>
                      <Label>Descripción</Label>
                      <Textarea
                        value={feature.description || ''}
                        onChange={(e) => updateFeature(idx, 'description', e.target.value)}
                        rows={2}
                        placeholder="Descripción de esta característica..."
                      />
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addFeature}>
                  <Plus size={16} className="mr-2" />
                  Agregar característica
                </Button>
                {(!formData.about?.features || formData.about.features.length === 0) && (
                  <p className="text-xs text-slate-500 mt-2">
                    Si no agregas características, se mostrarán los valores por defecto.
                  </p>
                )}
              </div>
            </div>

            {/* Estadísticas y Servicios */}
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900">Estadísticas y Servicios</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label>Estadísticas</Label>
                  <div className="mt-2 space-y-2">
                    {formData.company?.stats?.map((stat, idx) => (
                      <div key={idx} className="grid gap-2 md:grid-cols-3">
                        <Input
                          value={stat.value}
                          onChange={(e) => updateStat(idx, 'value', e.target.value)}
                          placeholder="Valor"
                        />
                        <Input
                          value={stat.label}
                          onChange={(e) => updateStat(idx, 'label', e.target.value)}
                          placeholder="Etiqueta"
                        />
                        <Button variant="outline" size="sm" onClick={() => removeStat(idx)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addStat}>
                      <Plus size={16} className="mr-2" />
                      Agregar estadística
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Servicios</Label>
                  <div className="mt-2 space-y-3">
                    {formData.services?.map((service, idx) => (
                      <div key={idx} className="rounded-lg border border-slate-200 p-3">
                        <div className="grid gap-2">
                          <Input
                            value={service.icon}
                            onChange={(e) => updateService(idx, 'icon', e.target.value)}
                            placeholder="Icono (Warehouse, Building2, etc.)"
                          />
                          <Input
                            value={service.title}
                            onChange={(e) => updateService(idx, 'title', e.target.value)}
                            placeholder="Título"
                          />
                          <Textarea
                            value={service.description}
                            onChange={(e) => updateService(idx, 'description', e.target.value)}
                            placeholder="Descripción"
                            rows={2}
                          />
                          <Button variant="outline" size="sm" onClick={() => removeService(idx)}>
                            <Trash2 size={16} className="mr-2" />
                            Eliminar servicio
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addService}>
                      <Plus size={16} className="mr-2" />
                      Agregar servicio
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Contacto Tab */}
          <TabsContent value="contacto" className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900">Información de Contacto</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.contact?.email || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contact: { ...formData.contact, email: e.target.value },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="whatsapp">WhatsApp (formato: 5491112345678)</Label>
                  <Input
                    id="whatsapp"
                    value={formData.contact?.whatsapp || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contact: { ...formData.contact, whatsapp: e.target.value },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="locationLine1">Ubicación - Línea 1</Label>
                  <Input
                    id="locationLine1"
                    value={formData.contact?.locationLine1 || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contact: { ...formData.contact, locationLine1: e.target.value },
                      })
                    }
                    placeholder="Habana 370, Oficina 101"
                  />
                </div>

                <div>
                  <Label htmlFor="locationLine2">Ubicación - Línea 2</Label>
                  <Input
                    id="locationLine2"
                    value={formData.contact?.locationLine2 || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contact: { ...formData.contact, locationLine2: e.target.value },
                      })
                    }
                    placeholder="Villa Martelli - Buenos Aires, Argentina"
                  />
                </div>

                <div>
                  <Label htmlFor="mapEmbed">Ubicación en Google Maps</Label>
                  <Input
                    id="mapEmbed"
                    value={formData.contact?.mapEmbed || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contact: { ...formData.contact, mapEmbed: e.target.value },
                      })
                    }
                    placeholder="Pega la URL de Google Maps aquí"
                  />
                  <div className="mt-2 rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
                    <p className="font-semibold mb-1">📍 Cómo obtener la URL:</p>
                    <ol className="list-decimal ml-4 space-y-1">
                      <li>Abre Google Maps → Busca tu ubicación</li>
                      <li>Clic en <strong>"Compartir"</strong> → <strong>"Insertar un mapa"</strong></li>
                      <li>Copia solo la URL del src="..." del código HTML</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Diseño Tab */}
          <TabsContent value="diseno" className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900">Diseño y Marca</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="primaryColor">Color Primario</Label>
                  <div className="mt-2 flex gap-2">
                    <Input
                      id="primaryColor"
                      value={formData.theme?.primaryColor || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          theme: { ...formData.theme, primaryColor: e.target.value },
                        })
                      }
                      placeholder="#1e3a5f"
                    />
                    <input
                      type="color"
                      value={formData.theme?.primaryColor || '#1e3a5f'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          theme: { ...formData.theme, primaryColor: e.target.value },
                        })
                      }
                      className="h-10 w-20 rounded-lg border border-slate-300 cursor-pointer"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="logo">Logo</Label>
                  <div className="mt-2 space-y-2">
                    {formData.theme?.logo && (
                      <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
                        <img src={formData.theme.logo} alt="Logo" className="h-12 bg-transparent" style={{ mixBlendMode: 'multiply' }} />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                      disabled={uploadingLogo}
                    />
                    {uploadingLogo && <p className="text-sm text-slate-600">Subiendo...</p>}
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                      <p className="font-semibold mb-1">💡 Recomendación importante:</p>
                      <p>Para mejores resultados, sube un logo en formato <strong>PNG con fondo transparente</strong>.</p>
                      <p className="mt-1">Si tu logo tiene fondo blanco, se verá un rectángulo blanco sobre fondos oscuros.</p>
                      <p className="mt-1">Herramientas gratuitas para quitar el fondo: <strong>remove.bg</strong></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-blue-200 bg-blue-50 p-6">
              <h3 className="text-lg font-semibold text-blue-900">Opciones de Tipografía</h3>
              <p className="mt-2 text-sm text-blue-800">
                Las opciones de tipografía (fuente, alineación y tamaño) se configuran individualmente para cada texto en las pestañas <strong>"Empresa"</strong> y <strong>"Contenido"</strong>.
              </p>
              <p className="mt-2 text-sm text-blue-700">
                Busca el icono de tipografía debajo de cada campo de texto para personalizar su estilo.
              </p>
            </div>
          </TabsContent>

          {/* Contenido Tab */}
          <TabsContent value="contenido" className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900">Contenido Hero</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="heroBadge">Badge</Label>
                  <Input
                    id="heroBadge"
                    value={formData.hero?.badge || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hero: { ...formData.hero, badge: e.target.value },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="heroTitle">Título Principal</Label>
                  <Textarea
                    id="heroTitle"
                    value={formData.hero?.title || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hero: { ...formData.hero, title: e.target.value },
                      })
                    }
                    rows={2}
                  />
                  <TextStyleOptions
                    label="título"
                    style={formData.hero?.titleStyle}
                    onChange={(newStyle) =>
                      setFormData({
                        ...formData,
                        hero: { ...formData.hero, titleStyle: newStyle },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="heroSubtitle">Subtítulo</Label>
                  <Textarea
                    id="heroSubtitle"
                    value={formData.hero?.subtitle || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hero: { ...formData.hero, subtitle: e.target.value },
                      })
                    }
                    rows={3}
                  />
                  <TextStyleOptions
                    label="subtítulo"
                    style={formData.hero?.subtitleStyle}
                    onChange={(newStyle) =>
                      setFormData({
                        ...formData,
                        hero: { ...formData.hero, subtitleStyle: newStyle },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="heroVideoUrl">Video de Presentación</Label>
                  <div className="mt-2 space-y-2">
                    <Input
                      id="heroVideoUrl"
                      value={formData.hero?.videoUrl || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          hero: { ...formData.hero, videoUrl: e.target.value },
                        })
                      }
                      placeholder="URL del video o sube uno abajo"
                    />
                    
                    {formData.hero?.videoUrl && (
                      <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                        <p className="text-xs font-medium text-slate-700 mb-2">Vista previa:</p>
                        <video
                          src={formData.hero.videoUrl}
                          className="w-full max-h-32 rounded-lg object-cover"
                          muted
                          loop
                          autoPlay
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="video/mp4,video/webm"
                        onChange={handleVideoUpload}
                        className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                        disabled={uploadingVideo}
                        id="videoUpload"
                      />
                    </div>
                    {uploadingVideo && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                          <span className="text-sm text-slate-600">{uploadProgress}%</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Subiendo video, por favor espera...</p>
                      </div>
                    )}
                    
                    {formData.hero?.videoUrl && (
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            hero: { ...formData.hero, videoUrl: '' },
                          })
                        }
                        className="text-xs text-red-600 hover:text-red-700 underline"
                      >
                        Eliminar video
                      </button>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    El video se reproducirá en loop de fondo en el hero (sin sonido). Formatos: MP4, WebM.
                    <br />
                    Recomendado: videos cortos (10-20 seg), optimizados, max 10-15 MB.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900">Footer</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="footerDescription">Descripción</Label>
                  <Textarea
                    id="footerDescription"
                    value={formData.footer?.description || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        footer: { ...formData.footer, description: e.target.value },
                      })
                    }
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Enlaces del Footer</Label>
                  <div className="mt-2 space-y-2">
                    {formData.footer?.links?.map((link, idx) => (
                      <div key={idx} className="grid gap-2 md:grid-cols-3">
                        <Input
                          value={link.label}
                          onChange={(e) => updateFooterLink(idx, 'label', e.target.value)}
                          placeholder="Etiqueta"
                        />
                        <Input
                          value={link.href}
                          onChange={(e) => updateFooterLink(idx, 'href', e.target.value)}
                          placeholder="/ruta"
                        />
                        <Button variant="outline" size="sm" onClick={() => removeFooterLink(idx)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addFooterLink}>
                      <Plus size={16} className="mr-2" />
                      Agregar enlace
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Páginas Tab */}
          <TabsContent value="paginas" className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900">Página de Contacto</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="contactTitle">Título Principal</Label>
                  <Input
                    id="contactTitle"
                    value={formData.contactPage?.title || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contactPage: { ...formData.contactPage, title: e.target.value },
                      })
                    }
                    placeholder="Coordinemos una llamada"
                  />
                </div>

                <div>
                  <Label htmlFor="contactSubtitle">Subtítulo</Label>
                  <Textarea
                    id="contactSubtitle"
                    value={formData.contactPage?.subtitle || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contactPage: { ...formData.contactPage, subtitle: e.target.value },
                      })
                    }
                    rows={2}
                    placeholder="Enviá tu consulta..."
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="formTitle">Título del Formulario</Label>
                    <Input
                      id="formTitle"
                      value={formData.contactPage?.formTitle || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contactPage: { ...formData.contactPage, formTitle: e.target.value },
                        })
                      }
                      placeholder="Formulario"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dataTitle">Título de Datos</Label>
                    <Input
                      id="dataTitle"
                      value={formData.contactPage?.dataTitle || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contactPage: { ...formData.contactPage, dataTitle: e.target.value },
                        })
                      }
                      placeholder="Datos"
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Label htmlFor="nameLabel">Etiqueta Nombre</Label>
                    <Input
                      id="nameLabel"
                      value={formData.contactPage?.nameLabel || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contactPage: { ...formData.contactPage, nameLabel: e.target.value },
                        })
                      }
                      placeholder="Nombre"
                    />
                  </div>

                  <div>
                    <Label htmlFor="emailLabel">Etiqueta Email</Label>
                    <Input
                      id="emailLabel"
                      value={formData.contactPage?.emailLabel || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contactPage: { ...formData.contactPage, emailLabel: e.target.value },
                        })
                      }
                      placeholder="Email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="messageLabel">Etiqueta Mensaje</Label>
                    <Input
                      id="messageLabel"
                      value={formData.contactPage?.messageLabel || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contactPage: { ...formData.contactPage, messageLabel: e.target.value },
                        })
                      }
                      placeholder="Mensaje"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="messagePlaceholder">Placeholder del Mensaje</Label>
                  <Textarea
                    id="messagePlaceholder"
                    value={formData.contactPage?.messagePlaceholder || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contactPage: { ...formData.contactPage, messagePlaceholder: e.target.value },
                      })
                    }
                    rows={2}
                    placeholder="Contanos qué necesitás..."
                  />
                </div>

                <div>
                  <Label htmlFor="submitButton">Texto del Botón de Envío</Label>
                  <Input
                    id="submitButton"
                    value={formData.contactPage?.submitButton || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contactPage: { ...formData.contactPage, submitButton: e.target.value },
                      })
                    }
                    placeholder="Enviar consulta"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Redes Sociales Tab */}
          <TabsContent value="redes" className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900">Redes Sociales</h3>
              <p className="mt-1 text-sm text-slate-600">
                Configura tus redes sociales. Aparecerán en el footer del sitio.
              </p>

              <div className="mt-6 space-y-6">
                {/* Instagram */}
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="text-base font-semibold text-slate-900">Instagram</div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm text-slate-600">Activado</span>
                      <input
                        type="checkbox"
                        checked={formData.socialMedia?.instagramEnabled ?? true}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            socialMedia: { ...formData.socialMedia, instagramEnabled: e.target.checked },
                          })
                        }
                        className="h-4 w-4"
                      />
                    </label>
                  </div>
                  <Input
                    value={formData.socialMedia?.instagram || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        socialMedia: { ...formData.socialMedia, instagram: e.target.value },
                      })
                    }
                    placeholder="https://instagram.com/tu-usuario"
                  />
                </div>

                {/* LinkedIn */}
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="text-base font-semibold text-slate-900">LinkedIn</div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm text-slate-600">Activado</span>
                      <input
                        type="checkbox"
                        checked={formData.socialMedia?.linkedinEnabled ?? true}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            socialMedia: { ...formData.socialMedia, linkedinEnabled: e.target.checked },
                          })
                        }
                        className="h-4 w-4"
                      />
                    </label>
                  </div>
                  <Input
                    value={formData.socialMedia?.linkedin || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        socialMedia: { ...formData.socialMedia, linkedin: e.target.value },
                      })
                    }
                    placeholder="https://linkedin.com/company/tu-empresa"
                  />
                </div>

                {/* YouTube */}
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="text-base font-semibold text-slate-900">YouTube</div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm text-slate-600">Activado</span>
                      <input
                        type="checkbox"
                        checked={formData.socialMedia?.youtubeEnabled ?? true}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            socialMedia: { ...formData.socialMedia, youtubeEnabled: e.target.checked },
                          })
                        }
                        className="h-4 w-4"
                      />
                    </label>
                  </div>
                  <Input
                    value={formData.socialMedia?.youtube || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        socialMedia: { ...formData.socialMedia, youtube: e.target.value },
                      })
                    }
                    placeholder="https://youtube.com/@tu-canal"
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};
